// Las cuatro curvas de las láminas 056A/056B (Adolescente Mujer/Hombre), calculadas sobre el historial de peso y talla del paciente:
// Peso/Edad (percentiles NCHS/2000), Talla/Edad e IMC/Edad (desviaciones estándar OMS 2007) y Velocidad de crecimiento (talla). El
// componente de gráfico se pide en diferido (recharts es pesado), como en `SignosVitalesTab`.
import type { Observation, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { curvaImcEdad10a19De, curvaPesoEdadNchs10a19De, curvaTallaEdad10a19De, type CurvaPercentilDatos } from '../../paciente/curvaPercentilPorEdad';
import { curvaVelocidadTallaDe } from '../../paciente/velocidadCrecimiento';

const CurvaPercentil = lazy(() => import('../../paciente/CurvaPercentil').then((m) => ({ default: m.CurvaPercentil })));

function Curva({ titulo, nota, datos, mensajeInsuficiente, etiquetaEjeY }: { readonly titulo: string; readonly nota?: string; readonly datos: CurvaPercentilDatos | undefined; readonly mensajeInsuficiente: string; readonly etiquetaEjeY: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-2">
      <h5 className="text-sm font-semibold">{titulo}</h5>
      {nota && <p className="text-xs text-hce-muted">{nota}</p>}
      {datos ? (
        <Suspense fallback={<p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>}>
          <CurvaPercentil datos={datos} etiquetaEjeY={etiquetaEjeY} ariaLabel={titulo} />
        </Suspense>
      ) : (
        <p className="text-sm text-muted-foreground">{mensajeInsuficiente}</p>
      )}
    </section>
  );
}

export function GraficasAdolescente({ subject, refresco, codigo }: { readonly subject: Reference<Patient>; /** Cambia al guardar una medición nueva, para releer el historial. */ readonly refresco: number; /** Hoja: el título de la velocidad de crecimiento cambia entre 056A (10 a 18 años) y 056B (0 a 18 años). */ readonly codigo: '056A' | '056B' }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const paciente = useResource<Patient>(subject);
  const [observaciones, setObservaciones] = useState<Observation[]>();

  useEffect(() => {
    let cancelado = false;
    medplum
      // Solo peso y talla (LOINC): con todos los signos vitales, un paciente muy medido perdería las tallas y pesos más antiguos por el tope.
      .searchResources('Observation', { patient: subject.reference ?? '', code: 'http://loinc.org|29463-7,http://loinc.org|8302-2', 'status:not': 'entered-in-error', _sort: '-date', _count: 200 })
      .then((lista) => !cancelado && setObservaciones(lista as Observation[]))
      .catch(() => !cancelado && setObservaciones([]));
    return () => {
      cancelado = true;
    };
  }, [medplum, subject.reference, refresco]);

  const lista = observaciones ?? [];
  const insuficiente = t('formularios.f056ab.graficas.insuficiente');
  const curvaPeso = paciente ? curvaPesoEdadNchs10a19De(lista, paciente) : undefined;
  const curvaTalla = paciente ? curvaTallaEdad10a19De(lista, paciente) : undefined;
  const curvaImc = paciente ? curvaImcEdad10a19De(lista, paciente) : undefined;
  const curvaVelocidad = paciente ? curvaVelocidadTallaDe(lista, paciente) : undefined;

  if (!paciente) return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  if (!paciente.birthDate || (paciente.gender !== 'male' && paciente.gender !== 'female')) {
    return <p className="text-sm text-hce-muted">{t('formularios.f056ab.graficas.faltanDatos')}</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <Curva titulo={t('formularios.f056ab.graficas.peso')} nota={t('formularios.f056ab.graficas.pesoNota')} datos={curvaPeso} mensajeInsuficiente={insuficiente} etiquetaEjeY="kg" />
      <Curva titulo={t('formularios.f056ab.graficas.talla')} nota={t('formularios.f056ab.graficas.tallaNota')} datos={curvaTalla} mensajeInsuficiente={insuficiente} etiquetaEjeY="cm" />
      <Curva titulo={t('formularios.f056ab.graficas.imc')} nota={t('formularios.f056ab.graficas.imcNota')} datos={curvaImc} mensajeInsuficiente={insuficiente} etiquetaEjeY="kg/m²" />
      <Curva
        titulo={t(`formularios.f056ab.graficas.velocidad${codigo}`)}
        nota={t('formularios.f056ab.graficas.velocidadNota')}
        datos={curvaVelocidad}
        mensajeInsuficiente={t('formularios.f056ab.graficas.velocidadInsuficiente')}
        etiquetaEjeY="cm/año"
      />
    </div>
  );
}
